import Links from '@/shared/constants/navegacao';
import axios, { isAxiosError } from 'axios';
import { API_URL } from '@/shared/constants/strings';
import { TOKEN_KEY } from '@/shared/constants/strings';
import { clearAuthSession, isPublicAuthRoute } from '@/modules/system/auth/application/auth-session.utils';
import { formatarErroChaveEstrangeira, normalizarMensagemErroApi } from '@/shared/utils/erros';

export const isAuthenticated = () => localStorage.getItem(TOKEN_KEY) !== null;
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const API_VERSION = '1';

/**
 * Retorno comum de uma api com many and count.
 */
export interface ApiReturn<T> {
  objects: T[];
  count: number;
}

export function GetApiReturn<T>(responsedata: Array<T>) {
  const r: ApiReturn<T> = { objects: [], count: 0 };
  if (responsedata) {
    r.objects = responsedata[0] as Array<T>;
    r.count = responsedata[1] as unknown as number;
  }
  return r;
}

export function GetApiReturnError<T>() {
  const r: ApiReturn<T> = { objects: [], count: 0 };
  return r;
}

/**
 * Interface com o servidor.
 */
const api = axios.create({ baseURL: API_URL as string });

api.interceptors.request.use((config) => {
  // Não definir Content-Type se for FormData - o navegador precisa definir com boundary
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json;charset=UTF-8';
  }
  //config.headers['Access-Control-Allow-Origin'] = '*';

  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function handleUnauthorizedResponse(): void {
  clearAuthSession();
  if (!isPublicAuthRoute()) {
    window.location.href = window.location.origin + `${Links.Auth_Login}`;
  }
}

/**
 * Define uma mensagem mais amigável para o usuário, e redireciona caso a resposta seja não autenticado.
 * @param error erro http.
 * @param mensagem mensagem amigável.
 */
export function ValidateAxiosError<T>(error: any, mensagem: string): T {
  const status = error?.response?.status;
  const dataMessage = error?.response?.data?.message;

  if (dataMessage) console.warn(dataMessage);

  if (status === 401) {
    handleUnauthorizedResponse();
    throw new Error('Não autenticado.');
  }

  if (status === 403) throw new Error('Acesso Negado!');

  if (isAxiosError(error)) {
    const mensagemErro = normalizarMensagemErroApi(dataMessage, mensagem);
    const mensagemFormatada = formatarErroChaveEstrangeira(mensagemErro);
    throw new Error(mensagemFormatada);
  }
  throw new Error(mensagem);
}

export function handleApiError(error: unknown): never {
  if (isAxiosError(error) && error.response) {
    if (error.response.status === 401) {
      handleUnauthorizedResponse();
      throw new Error('Não autenticado.');
    }

    if (error.response.status === 403) throw new Error('Acesso Negado!');

    console.error('Erro na API:', error.response.status, error.response.data);
    const mensagemErro = normalizarMensagemErroApi(error.response?.data?.message, 'Erro desconhecido');
    const mensagemFormatada = formatarErroChaveEstrangeira(mensagemErro);
    throw new Error(mensagemFormatada);
  }
  console.error('Erro desconhecido:', error);
  throw new Error('Erro desconhecido');
}

/**
 * Define uma mensagem mais amigável para o usuário, e redireciona caso a resposta seja não autenticado.
 * @param error erro http.
 * @param mensagem mensagem amigável.
 */
export function ValidateAxiosErrorSemRedirecionar<T>(
  error: any,
  mensagem: string,
): T {
  const status = error?.response?.status;
  if (status) console.error(status);
  if (status === 403) throw new Error('Acesso Negado!');
  const mensagemErro = normalizarMensagemErroApi(error?.response?.data?.message, mensagem);
  const mensagemFormatada = formatarErroChaveEstrangeira(mensagemErro);
  throw new Error(mensagemFormatada);
}

export default api;
